import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    School,
    MapPin,
    Users,
    Upload,
    CheckCircle,
    AlertCircle,
    Loader2,
    Info,
    Search,
    X,
    Building,
    FileImage
} from 'lucide-react';
import schoolCache from '../utils/schoolCache';
import { getGeoPosition } from '../utils/getGeoPosition';
import toast from '../utils/toast';
import FormInput from '../components/FormInput';
import FormTextArea from '../components/FormTextArea';
import FormSelect from '../components/FormSelect';
import { validateForm, commonValidationRules } from '../utils/formValidation';

const SchoolEnrollment = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        schoolId: '',
        name: '',
        region: '',
        district: '',
        type: '',
        management: '',
        isMadarassa: false,
        clusterName: '',
        classification: '',
        latitude: null,
        longitude: null,
        accuracy: null,
        address: '',
        studentCount: '',
        signupType: 'individual' // 'all_students' or 'individual'
    });

    const [selectedSchool, setSelectedSchool] = useState(null);
    const [schoolSearch, setSchoolSearch] = useState('');
    const [schoolResults, setSchoolResults] = useState([]);
    const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
    const [isNewSchool, setIsNewSchool] = useState(false);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [allSchools, setAllSchools] = useState([]);
    const [schoolsLoaded, setSchoolsLoaded] = useState(false);
    const [geolocationStatus, setGeolocationStatus] = useState('pending');
    const [geolocationError, setGeolocationError] = useState('');
    const [errors, setErrors] = useState({});
    const [submitStatus, setSubmitStatus] = useState('');

    // Load all schools from cache
    useEffect(() => {
        const unsubscribe = schoolCache.subscribe((cacheData) => {
            setAllSchools(cacheData.schools || []);
            setSchoolsLoaded(!cacheData.isLoading);
        });

        // Load schools if not already loaded
        if (schoolCache.schools.length === 0) {
            schoolCache.loadSchools();
        } else {
            setAllSchools(schoolCache.schools);
            setSchoolsLoaded(true);
        }

        return unsubscribe;
    }, []);

    // Client-side school search effect using cache with incremental paging
    const [searchPage, setSearchPage] = useState(1);
    const resultsPerPage = 20;

    useEffect(() => {
        if (!schoolsLoaded || schoolSearch.length < 1) {
            setSchoolResults([]);
            setShowSchoolDropdown(false);
            setSearchPage(1);
            return;
        }

        const pageSize = searchPage * resultsPerPage;
        const filteredSchools = schoolCache.searchSchools(schoolSearch, pageSize);
        setSchoolResults(filteredSchools);
        setShowSchoolDropdown(filteredSchools.length > 0 || schoolSearch.length >= 2);
    }, [schoolSearch, schoolsLoaded, searchPage]);

    // Geolocation effect - don't auto-request, wait for user action
    useEffect(() => {
        if (!navigator.geolocation) {
            setGeolocationStatus('error');
            setGeolocationError('Geolocation is not supported by this browser.');
            return;
        }

        if (!window.isSecureContext) {
            setGeolocationStatus('error');
            setGeolocationError('Geolocation requires a secure connection (HTTPS). Please access this site via https://amhas.org');
            return;
        }

        // Check if we're in an iframe and geolocation isn't allowed
        const inIframe = window.top !== window.self;
        const geoAllowed = document.permissionsPolicy?.allowsFeature?.('geolocation') ?? true;

        if (inIframe && !geoAllowed) {
            setGeolocationStatus('error');
            setGeolocationError('This page is embedded in a site that blocks location. Open it directly at https://amhas.org or allow geolocation for the embed.');
            return;
        }

        // Check if geolocation is already approved
        if (navigator.permissions?.query) {
            navigator.permissions.query({ name: 'geolocation' }).then((status) => {
                if (status.state === 'granted') {
                    setGeolocationStatus('approved');
                } else if (status.state === 'denied') {
                    setGeolocationStatus('denied');
                    setGeolocationError('Location access denied. Please click the location icon in your browser address bar and allow access, then try again.');
                } else {
                    setGeolocationStatus('pending');
                }
            }).catch(() => {
                // Permissions API not supported, start with pending
                setGeolocationStatus('pending');
            });
        } else {
            setGeolocationStatus('pending');
        }
    }, []);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSchoolSelect = (school) => {
        setSelectedSchool(school);
        setFormData(prev => ({
            ...prev,
            schoolId: school.id,
            name: school.name,
            region: school.region,
            district: school.district,
            type: school.type || '',
            management: school.management || '',
            address: school.address || '',
            latitude: school.latitude,
            longitude: school.longitude
        }));
        setSchoolSearch(school.name);
        setShowSchoolDropdown(false);
        setIsNewSchool(false);
        if (errors.schoolId) {
            setErrors(prev => ({ ...prev, schoolId: null }));
        }
    };

    const handleNewSchool = () => {
        setIsNewSchool(true);
        setSelectedSchool(null);
        setFormData(prev => ({
            ...prev,
            schoolId: '',
            name: schoolSearch,
            region: '',
            district: '',
            type: '',
            management: '',
            isMadarassa: false,
            clusterName: '',
            classification: '',
            latitude: null,
            longitude: null,
            address: ''
        }));
        setShowSchoolDropdown(false);
    };

    // IP-based geolocation fallback (same as StudentEnrollment)
    const testIPGeolocation = async () => {
        console.log('🌍 [SCHOOL-IP] Testing IP-based geolocation...');
        try {
            const response = await fetch('https://ipapi.co/json/');
            const data = await response.json();
            console.log('🌍 [SCHOOL-IP] IP Geolocation result:', data);

            if (data.latitude && data.longitude) {
                console.log('🌍 [SCHOOL-IP] ✅ IP coordinates found:', {
                    latitude: data.latitude,
                    longitude: data.longitude,
                    city: data.city,
                    country: data.country_name
                });

                // Populate the form with IP-based location
                setFormData(prev => ({
                    ...prev,
                    latitude: data.latitude,
                    longitude: data.longitude,
                    accuracy: 10000 // IP geolocation is typically accurate to ~10km
                }));
                setGeolocationStatus('approved');
                console.log('🌍 [SCHOOL-IP] ✅ Form populated with IP location');
            }
        } catch (error) {
            console.error('🌍 [SCHOOL-IP] ❌ IP geolocation failed:', error);
            setGeolocationStatus('error');
            setGeolocationError('Unable to get location via GPS or IP. Please enter coordinates manually.');
        }
    };

    const requestGeolocation = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        setGeolocationError('');
        setGeolocationStatus('pending');

        if (!navigator.geolocation) {
            setGeolocationStatus('error');
            setGeolocationError('Geolocation is not supported by this browser.');
            return;
        }

        // Use simple, direct geolocation approach (same as StudentEnrollment)
        navigator.geolocation.getCurrentPosition(
            (position) => {
                console.log('✅ School geolocation success:', {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy
                });

                setFormData(prev => ({
                    ...prev,
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy
                }));
                setGeolocationStatus('approved');
            },
            (error) => {
                console.error('❌ School geolocation failed:', error);
                console.error('❌ Error code:', error.code);
                console.error('❌ Error message:', error.message);

                // If simple fails, try IP geolocation as fallback (same as StudentEnrollment)
                console.log('🔄 Trying IP geolocation fallback...');
                testIPGeolocation();
            },
            {
                enableHighAccuracy: false,
                timeout: 20000,
                maximumAge: 300000 // 5 minutes cache
            }
        );
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setLogoPreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const validateFormData = () => {
        const newErrors = {};

        // Use standardized validation for core fields
        const coreErrors = validateForm(formData, commonValidationRules.schoolEnrollment);
        Object.assign(newErrors, coreErrors);

        // Additional validation for school-specific fields
        if (!formData.signupType) newErrors.signupType = 'Please select how students will sign up';

        // If it's a new school, validate additional fields
        if (isNewSchool || !formData.schoolId) {
            if (!formData.region?.trim()) newErrors.region = 'Region is required';
            if (!formData.district?.trim()) newErrors.district = 'District is required';
            if (!formData.type.trim()) newErrors.type = 'School type is required';
            if (!formData.management.trim()) newErrors.management = 'Management type is required';
            if (!formData.address.trim()) newErrors.address = 'Address is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateFormData()) {
            return;
        }

        setLoading(true);
        setSubmitStatus('');

        try {
            const formDataToSend = new FormData();

            // Append all form fields
            Object.keys(formData).forEach(key => {
                if (formData[key] !== null && formData[key] !== '') {
                    formDataToSend.append(key, formData[key]);
                }
            });

            // Append logo file if present
            if (logoFile) {
                formDataToSend.append('logo', logoFile);
            }

            // Debug: Log form data being sent
            console.log('Form data being sent:', formData);
            console.log('FormData entries:');
            for (let [key, value] of formDataToSend.entries()) {
                console.log(`${key}:`, value);
            }

            const response = await fetch('/api/enrollments/school', {
                method: 'POST',
                body: formDataToSend
            });

            if (response.ok) {
                const result = await response.json();
                toast.success('School enrollment successful!');

                // Navigate to success page
                navigate('/enrollment-success', {
                    state: {
                        enrollmentType: 'school',
                        schoolName: formData.name || selectedSchool?.name
                    }
                });
            } else if (response.status === 429) {
                toast.error('Too many requests. Please wait a moment and try again.');
                setSubmitStatus('error');
            } else {
                try {
                    const errorData = await response.json();
                    console.error('Enrollment error (400):', errorData);
                    console.error('Response status:', response.status);
                    console.error('Response headers:', response.headers);

                    // Show detailed error message
                    if (errorData.details && Array.isArray(errorData.details)) {
                        const errorMessages = errorData.details.join(', ');
                        toast.error(`Validation errors: ${errorMessages}`);

                        // Map detailed errors to form fields
                        const fieldErrors = {};
                        errorData.details.forEach(error => {
                            console.error('Processing error:', error);
                            if (error.includes('name')) fieldErrors.name = error;
                            else if (error.includes('students')) fieldErrors.studentCount = error;
                            else if (error.includes('Region')) fieldErrors.region = error;
                            else if (error.includes('District')) fieldErrors.district = error;
                            else if (error.includes('type')) fieldErrors.type = error;
                            else if (error.includes('Management')) fieldErrors.management = error;
                            else if (error.includes('Address')) fieldErrors.address = error;
                            else if (error.includes('signup')) fieldErrors.signupType = error;
                        });
                        setErrors(fieldErrors);
                    } else {
                        toast.error(errorData.error || 'Failed to submit enrollment. Please try again.');
                    }
                    setSubmitStatus('error');
                } catch (parseError) {
                    console.error('Error parsing response:', parseError);
                    console.error('Response status:', response.status);
                    console.error('Response statusText:', response.statusText);
                    toast.error('Server error. Please try again later.');
                    setSubmitStatus('error');
                }
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error('Network error. Please check your connection and try again.');
            setSubmitStatus('error');
        } finally {
            setLoading(false);
        }
    };

    const Tooltip = ({ children, text }) => (
        <div className="relative group">
            {children}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 whitespace-nowrap max-w-xs">
                {text}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pt-24 pb-12">
            <div className="container-custom">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="max-w-3xl mx-auto"
                >
                    {/* Logo and Title */}
                    <div className="text-center mb-12">
                        <motion.img
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.6 }}
                            src="/amhas-logo.png"
                            alt="AMHAS Logo"
                            className="w-24 h-24 mx-auto mb-4"
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                                AMHAS
                            </h1>
                            <h2 className="text-3xl md:text-4xl font-bold text-primary-600 mb-4">
                                School Enrollment Form
                            </h2>
                            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                                Register your school for early access to AMHAS healthcare services.
                                Get priority launch status and significant discounts for your students and community.
                            </p>
                        </motion.div>
                    </div>

                    {/* Form Container with Animated Border */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="relative"
                    >

                        <div className="relative bg-white rounded-2xl p-8 m-1">
                            {/* Success Message */}
                            {submitStatus === 'success' && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg"
                                >
                                    <div className="flex items-center space-x-2">
                                        <CheckCircle size={20} className="text-green-600" />
                                        <span className="text-green-800 font-medium">
                                            School enrollment submitted successfully! We'll contact you soon with next steps.
                                        </span>
                                    </div>
                                </motion.div>
                            )}

                            {/* Error Message */}
                            {submitStatus === 'error' && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
                                >
                                    <div className="flex items-center space-x-2">
                                        <AlertCircle size={20} className="text-red-600" />
                                        <span className="text-red-800 font-medium">
                                            There was an error submitting your enrollment. Please check your information and try again.
                                        </span>
                                    </div>
                                </motion.div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-8">
                                {/* School Search/Selection */}
                                <div className="space-y-4">
                                    <h3 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                                        <School size={20} className="text-primary-600" />
                                        <span>School Information</span>
                                    </h3>

                                    <div className="relative">
                                        <Tooltip text="Search for your school in our database, or add it as a new school">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                School Name *
                                                <Info size={14} className="inline ml-1 text-gray-400" />
                                            </label>
                                        </Tooltip>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={schoolSearch}
                                                onChange={(e) => setSchoolSearch(e.target.value)}
                                                onFocus={() => (schoolResults.length > 0 || schoolSearch.length >= 2) && setShowSchoolDropdown(true)}
                                                className={`w-full px-4 py-3 pl-12 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.name ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Search for your school..."
                                            />
                                            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        </div>

                                        {/* School Dropdown */}
                                        {showSchoolDropdown && (
                                            <div
                                                className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto custom-scrollbar"
                                                onScroll={(e) => {
                                                    const el = e.currentTarget;
                                                    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
                                                        setSearchPage(prev => prev + 1);
                                                    }
                                                }}
                                            >
                                                {schoolResults.map((school) => (
                                                    <button
                                                        key={school.id}
                                                        type="button"
                                                        onClick={() => handleSchoolSelect(school)}
                                                        className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                                                    >
                                                        <div className="font-medium text-gray-900">{school.name}</div>
                                                        <div className="text-sm text-gray-600">{school.district}, {school.region}</div>
                                                    </button>
                                                ))}

                                                {/* Show "Add as new school" when there are results OR when there are no results but user has typed */}
                                                {(schoolResults.length > 0 || schoolSearch.length >= 2) && (
                                                    <button
                                                        type="button"
                                                        onClick={handleNewSchool}
                                                        className="w-full px-4 py-3 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-t border-blue-200 bg-blue-25"
                                                    >
                                                        <div className="font-medium text-blue-600">+ Add "{schoolSearch}" as new school</div>
                                                        <div className="text-sm text-blue-500">Click to add this school to our database</div>
                                                    </button>
                                                )}

                                                {/* Show "No results" message when there are no results and user has typed */}
                                                {schoolResults.length === 0 && schoolSearch.length >= 2 && (
                                                    <div className="px-4 py-3 text-center text-gray-500">
                                                        <p>No schools found matching "{schoolSearch}"</p>
                                                        <p className="text-sm mt-1">Use the button above to add it as a new school</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {selectedSchool && !isNewSchool && (
                                            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                                <div className="flex items-center space-x-2">
                                                    <CheckCircle size={16} className="text-green-600" />
                                                    <span className="text-green-800 font-medium">Selected School:</span>
                                                </div>
                                                <div className="mt-1 text-sm text-gray-700">
                                                    <div>{selectedSchool.name}</div>
                                                    <div>{selectedSchool.district}, {selectedSchool.region}</div>
                                                </div>
                                            </div>
                                        )}

                                        {errors.name && (
                                            <p className="text-red-600 text-sm mt-1">{errors.name}</p>
                                        )}
                                    </div>
                                </div>

                                {/* New School Details */}
                                {isNewSchool && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        transition={{ duration: 0.3 }}
                                        className="space-y-4"
                                    >
                                        <h3 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                                            <Building size={20} className="text-accent-600" />
                                            <span>New School Details</span>
                                        </h3>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <Tooltip text="Geographic region where the school is located">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Region *
                                                        <Info size={14} className="inline ml-1 text-gray-400" />
                                                    </label>
                                                </Tooltip>
                                                <input
                                                    type="text"
                                                    name="region"
                                                    value={formData.region}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.region ? 'border-red-500' : 'border-gray-300'
                                                        }`}
                                                    placeholder="e.g., Greater Banjul Area"
                                                />
                                                {errors.region && (
                                                    <p className="text-red-600 text-sm mt-1">{errors.region}</p>
                                                )}
                                            </div>

                                            <div>
                                                <Tooltip text="Administrative district within the region">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        District *
                                                        <Info size={14} className="inline ml-1 text-gray-400" />
                                                    </label>
                                                </Tooltip>
                                                <input
                                                    type="text"
                                                    name="district"
                                                    value={formData.district}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.district ? 'border-red-500' : 'border-gray-300'
                                                        }`}
                                                    placeholder="e.g., Banjul"
                                                />
                                                {errors.district && (
                                                    <p className="text-red-600 text-sm mt-1">{errors.district}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <Tooltip text="Type of educational institution (Primary, Secondary, etc.)">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        School Type *
                                                        <Info size={14} className="inline ml-1 text-gray-400" />
                                                    </label>
                                                </Tooltip>
                                                <select
                                                    name="type"
                                                    value={formData.type}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.type ? 'border-red-500' : 'border-gray-300'
                                                        }`}
                                                >
                                                    <option value="">Select school type</option>
                                                    <option value="Primary">Primary</option>
                                                    <option value="Secondary">Secondary</option>
                                                    <option value="Combined">Combined (Primary & Secondary)</option>
                                                    <option value="Technical">Technical/Vocational</option>
                                                </select>
                                                {errors.type && (
                                                    <p className="text-red-600 text-sm mt-1">{errors.type}</p>
                                                )}
                                            </div>

                                            <div>
                                                <Tooltip text="Who manages the school (Government, Private, Mission, etc.)">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Management *
                                                        <Info size={14} className="inline ml-1 text-gray-400" />
                                                    </label>
                                                </Tooltip>
                                                <select
                                                    name="management"
                                                    value={formData.management}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.management ? 'border-red-500' : 'border-gray-300'
                                                        }`}
                                                >
                                                    <option value="">Select management type</option>
                                                    <option value="Government">Government</option>
                                                    <option value="Private">Private</option>
                                                    <option value="Mission">Mission</option>
                                                    <option value="Community">Community</option>
                                                    <option value="NGO">NGO</option>
                                                </select>
                                                {errors.management && (
                                                    <p className="text-red-600 text-sm mt-1">{errors.management}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <Tooltip text="Educational cluster or zone the school belongs to">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Cluster Name
                                                        <Info size={14} className="inline ml-1 text-gray-400" />
                                                    </label>
                                                </Tooltip>
                                                <input
                                                    type="text"
                                                    name="clusterName"
                                                    value={formData.clusterName}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                                    placeholder="e.g., Central Cluster"
                                                />
                                            </div>

                                            <div>
                                                <Tooltip text="Official classification of the school">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Classification
                                                        <Info size={14} className="inline ml-1 text-gray-400" />
                                                    </label>
                                                </Tooltip>
                                                <input
                                                    type="text"
                                                    name="classification"
                                                    value={formData.classification}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                                    placeholder="e.g., Senior Secondary"
                                                />
                                            </div>

                                            <div>
                                                <Tooltip text="Check if this is an Islamic school (Madarassa)">
                                                    <label className="flex items-center space-x-3 text-sm font-medium text-gray-700 mt-8">
                                                        <input
                                                            type="checkbox"
                                                            name="isMadarassa"
                                                            checked={formData.isMadarassa}
                                                            onChange={handleInputChange}
                                                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                                        />
                                                        <span>Is Madarassa</span>
                                                        <Info size={14} className="text-gray-400" />
                                                    </label>
                                                </Tooltip>
                                            </div>
                                        </div>

                                        <div>
                                            <Tooltip text="Complete physical address of the school">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Address *
                                                    <Info size={14} className="inline ml-1 text-gray-400" />
                                                </label>
                                            </Tooltip>
                                            <textarea
                                                name="address"
                                                value={formData.address}
                                                onChange={handleInputChange}
                                                rows="3"
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.address ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Enter complete school address"
                                            />
                                            {errors.address && (
                                                <p className="text-red-600 text-sm mt-1">{errors.address}</p>
                                            )}
                                        </div>

                                        {/* Location Information */}
                                        <div className="space-y-4">
                                            <h4 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
                                                <MapPin size={18} className="text-accent-600" />
                                                <span>Location Information</span>
                                            </h4>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Latitude (X Coordinate)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        name="latitude"
                                                        value={formData.latitude || ''}
                                                        onChange={handleInputChange}
                                                        step="any"
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                                        placeholder="Enter latitude (e.g., 13.4549)"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Longitude (Y Coordinate)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        name="longitude"
                                                        value={formData.longitude || ''}
                                                        onChange={handleInputChange}
                                                        step="any"
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                                        placeholder="Enter longitude (e.g., -16.5790)"
                                                    />
                                                </div>
                                            </div>

                                            <div className="flex items-center space-x-4">
                                                <button
                                                    type="button"
                                                    onClick={requestGeolocation}
                                                    disabled={geolocationStatus === 'pending'}
                                                    className="btn-secondary text-sm py-2 px-4 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {geolocationStatus === 'pending' ? (
                                                        <>
                                                            <Loader2 size={16} className="animate-spin" />
                                                            <span>Getting Location...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <MapPin size={16} />
                                                            <span>Get Current Location</span>
                                                        </>
                                                    )}
                                                </button>

                                                {formData.accuracy && (
                                                    <span className="text-sm text-gray-600">
                                                        Accuracy: ±{formData.accuracy.toFixed(0)}m
                                                    </span>
                                                )}
                                            </div>

                                            <p className="text-sm text-gray-600">
                                                Enter the school's coordinates manually or use the button to get your current location automatically.
                                            </p>
                                        </div>
                                    </motion.div>
                                )}

                                {/* Enrollment Details */}
                                <div className="space-y-4">
                                    <h3 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                                        <Users size={20} className="text-accent-600" />
                                        <span>Enrollment Details</span>
                                    </h3>

                                    <div>
                                        <Tooltip text="Total number of students in your school who could benefit from AMHAS">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Number of Students *
                                                <Info size={14} className="inline ml-1 text-gray-400" />
                                            </label>
                                        </Tooltip>
                                        <input
                                            type="number"
                                            name="studentCount"
                                            value={formData.studentCount}
                                            onChange={handleInputChange}
                                            min="1"
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.studentCount ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Enter total number of students"
                                        />
                                        {errors.studentCount && (
                                            <p className="text-red-600 text-sm mt-1">{errors.studentCount}</p>
                                        )}
                                    </div>

                                    <div>
                                        <Tooltip text="Choose how students will enroll - either you sign up all students at once, or students sign up individually">
                                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                                Student Signup Method *
                                                <Info size={14} className="inline ml-1 text-gray-400" />
                                            </label>
                                        </Tooltip>
                                        <div className="space-y-3">
                                            <label className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="signupType"
                                                    value="all_students"
                                                    checked={formData.signupType === 'all_students'}
                                                    onChange={handleInputChange}
                                                    className="mt-1 w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                                                />
                                                <div>
                                                    <div className="font-medium text-gray-900">Sign up all students</div>
                                                    <div className="text-sm text-gray-600">
                                                        You'll enroll all students at once. Great for streamlined administration.
                                                    </div>
                                                </div>
                                            </label>

                                            <label className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="signupType"
                                                    value="individual"
                                                    checked={formData.signupType === 'individual'}
                                                    onChange={handleInputChange}
                                                    className="mt-1 w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                                                />
                                                <div>
                                                    <div className="font-medium text-gray-900">Let students sign up individually</div>
                                                    <div className="text-sm text-gray-600">
                                                        Students/parents will sign up individually using a custom page for your school.
                                                    </div>
                                                </div>
                                            </label>
                                        </div>
                                        {errors.signupType && (
                                            <p className="text-red-600 text-sm mt-1">{errors.signupType}</p>
                                        )}
                                    </div>
                                </div>

                                {/* School Logo Upload */}
                                {formData.signupType === 'individual' && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        transition={{ duration: 0.3 }}
                                        className="space-y-4"
                                    >
                                        <h3 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                                            <FileImage size={20} className="text-secondary-600" />
                                            <span>School Logo (Optional)</span>
                                        </h3>

                                        <div>
                                            <Tooltip text="Upload your school logo to be displayed on your custom enrollment page">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Upload School Logo
                                                    <Info size={14} className="inline ml-1 text-gray-400" />
                                                </label>
                                            </Tooltip>

                                            <div className="flex items-center space-x-4">
                                                <label className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                                                    <Upload size={16} className="text-gray-400" />
                                                    <span className="text-sm text-gray-600">Choose File</span>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleLogoChange}
                                                        className="hidden"
                                                    />
                                                </label>

                                                {logoFile && (
                                                    <span className="text-sm text-gray-600">
                                                        {logoFile.name}
                                                    </span>
                                                )}
                                            </div>

                                            {logoPreview && (
                                                <div className="mt-4">
                                                    <img
                                                        src={logoPreview}
                                                        alt="Logo preview"
                                                        className="w-24 h-24 object-contain border border-gray-200 rounded-lg"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}

                                {/* Consent Checkbox */}
                                <div className="space-y-4">
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                        <label className="flex items-start space-x-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                required
                                                className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2 mt-1"
                                            />
                                            <div className="text-sm text-gray-700">
                                                <span className="font-medium">I agree to receive promotional materials, updates, and communications</span>
                                                <p className="text-gray-600 mt-1">
                                                    By checking this box, you consent to receive emails, text messages, and promotional materials about AMHAS services, launch updates, and educational content. You can unsubscribe at any time.
                                                </p>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="pt-6">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full btn-primary py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 size={20} className="animate-spin" />
                                                <span>Submitting Enrollment...</span>
                                            </>
                                        ) : (
                                            <>
                                                <School size={20} />
                                                <span>Submit School Enrollment</span>
                                            </>
                                        )}
                                    </button>

                                    <p className="text-center text-sm text-gray-600 mt-4">
                                        By submitting, you're registering your school for early access and helping us plan regional launches.
                                    </p>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
};

export default SchoolEnrollment;
